/**
 * Die Admin-Oberfläche — der Einstieg des 47-Schritte-Durchlaufs (§67 Schritte 2 und 3).
 *
 * **Diese Schale entscheidet nur eines: ob der Schlüssel da ist.** Alles andere liegt in den
 * beiden Bereichen darunter — die globale Regelkonfiguration (§52) und die Host-Links.
 *
 * **Ohne Schlüssel wird der Dienst nicht gefragt.** Eine Anfrage ohne Berechtigung bekäme 401,
 * und der Admin sähe eine Abweisung, obwohl er noch nichts eingegeben hat.
 *
 * **Ein abgewiesener Schlüssel wird vergessen, nicht wiederholt.** Der Dienst antwortet auf einen
 * fehlenden und einen falschen Schlüssel **absichtlich gleich** (`auth/admin.ts`: wer rät, soll
 * nichts dazulernen) — diese Oberfläche kann die Fälle also nicht unterscheiden und behauptet
 * auch nicht, es zu können: der Text nennt beide. Der zweite Fall ist ein **gültiger
 * Betriebszustand**, kein Fehler: ein Dienst ohne `ADMIN_KEY` bedient Partien, erlaubt aber keine
 * Regeländerungen.
 *
 * **Der Schlüssel kommt aus einem Formular und steht in keiner Adresse** (`ARCHITECTURE.md`
 * **E26**). Er lebt im `sessionStorage` dieses Tabs (`admin/key.ts`) — er ist der mächtigste Wert
 * dieser Anwendung und öffnet ab Phase 11 den Debug-Modus, also eine Cheat-API.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HostTokenList } from '../admin/HostTokenList.js';
import { PublisherKeyList } from '../admin/PublisherKeyList.js';
import { RuleConfigForm } from '../admin/RuleConfigForm.js';
import { clearAdminKey, readAdminKey, writeAdminKey } from '../admin/key.js';

interface KeyFormProps {
  readonly rejected: boolean;
  readonly onSubmit: (key: string) => void;
}

function AdminKeyForm({ rejected, onSubmit }: KeyFormProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [value, setValue] = useState('');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (value.trim() !== '') onSubmit(value);
      }}
    >
      <h2>{t('admin:key.heading')}</h2>
      <p>{t('admin:key.intro')}</p>
      {rejected ? <p role="alert">{t('admin:key.rejected')}</p> : null}
      <p>
        <label>
          {t('admin:key.label')}{' '}
          <input
            // `password`, damit er nicht über die Schulter mitgelesen wird und nicht in der
            // Formularchronik des Browsers landet.
            type="password"
            autoComplete="off"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </label>{' '}
        <button type="submit" disabled={value.trim() === ''}>
          {t('admin:key.submit')}
        </button>
      </p>
      <p>{t('admin:key.storageHint')}</p>
    </form>
  );
}

export function AdminScreen() {
  const { t } = useTranslation(['admin', 'common']);
  const [adminKey, setAdminKey] = useState<string | undefined>(() => readAdminKey());
  const [rejected, setRejected] = useState(false);

  const open = useCallback((key: string): void => {
    writeAdminKey(key);
    setRejected(false);
    setAdminKey(readAdminKey());
  }, []);

  /** Der Schlüssel gilt nicht — vergessen und erneut fragen. */
  const onUnauthenticated = useCallback((): void => {
    clearAdminKey();
    setAdminKey(undefined);
    setRejected(true);
  }, []);

  const forget = useCallback((): void => {
    clearAdminKey();
    setAdminKey(undefined);
    setRejected(false);
  }, []);

  return (
    <main>
      <h1>{t('admin:heading')}</h1>
      {adminKey === undefined ? (
        <AdminKeyForm rejected={rejected} onSubmit={open} />
      ) : (
        <>
          <p>{t('admin:intro')}</p>
          <p>
            <button type="button" onClick={forget}>
              {t('admin:key.forget')}
            </button>
          </p>
          <RuleConfigForm adminKey={adminKey} onUnauthenticated={onUnauthenticated} />
          <HostTokenList adminKey={adminKey} onUnauthenticated={onUnauthenticated} />
          <PublisherKeyList adminKey={adminKey} onUnauthenticated={onUnauthenticated} />
        </>
      )}
    </main>
  );
}
